import React, { useState, useEffect } from 'react';
import {
  Box,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  FormField,
  Input,
  ProgressBar,
  LineChart
} from '@cloudscape-design/components';

const CostSavingsEstimator = () => {
  // State for calculator inputs
  const [inputs, setInputs] = useState({
    currentInfrastructureCost: 240000,
    expectedInfrastructureCost: 140000,
    developmentHourRate: 150,
    deploymentFrequency: 480, // deployments per year
    currentDeploymentTime: 2, // hours
    expectedDeploymentTime: 0.25, // hours
  });

  // State for calculated values
  const [calculations, setCalculations] = useState({
    infrastructureSavings: 0,
    timeSavings: 0,
    totalSavings: 0,
    roi: 0,
  });

  // Dummy data for savings trend
  const trendData = [
    { x: 'Jan', y: 15000 },
    { x: 'Feb', y: 22000 },
    { x: 'Mar', y: 28000 },
    { x: 'Apr', y: 32000 },
  ];

  // Calculate savings whenever inputs change
  useEffect(() => {
    const infrastructureSavings = inputs.currentInfrastructureCost - inputs.expectedInfrastructureCost;
    const timeSavingsHours = inputs.deploymentFrequency * (inputs.currentDeploymentTime - inputs.expectedDeploymentTime);
    const timeSavingsCost = timeSavingsHours * inputs.developmentHourRate;
    const totalSavings = infrastructureSavings + timeSavingsCost;
    const roi = (totalSavings / inputs.currentInfrastructureCost) * 100;

    setCalculations({
      infrastructureSavings,
      timeSavings: timeSavingsCost,
      totalSavings,
      roi
    });
  }, [inputs]);

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Calculate potential cost savings from modernization"
        >
          Cost Savings Estimator
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Summary Cards */}
        <ColumnLayout columns={2} variant="text-grid">
          <Box
            padding="s"
            textAlign="center"
            variant="awsui-key-label"
          >
            <SpaceBetween size="xs">
              <Box variant="h3">
                ${calculations.totalSavings.toLocaleString()}
              </Box>
              <Box>Estimated Annual Savings</Box>
            </SpaceBetween>
          </Box>
          <Box
            padding="s"
            textAlign="center"
            variant="awsui-key-label"
          >
            <SpaceBetween size="xs">
              <Box variant="h3">
                {calculations.roi.toFixed(1)}%
              </Box>
              <Box>Return on Investment</Box>
            </SpaceBetween>
          </Box>
        </ColumnLayout>

        {/* Savings Breakdown */}
        <SpaceBetween size="s">
          <Box variant="h4">Savings Breakdown</Box>
          <FormField label="Infrastructure Savings">
            <ProgressBar
              value={calculations.infrastructureSavings}
              label={`$${calculations.infrastructureSavings.toLocaleString()}`}
              maxValue={inputs.currentInfrastructureCost}
            />
          </FormField>
          <FormField label="Development Time Savings">
            <ProgressBar
              value={calculations.timeSavings}
              label={`$${calculations.timeSavings.toLocaleString()}`}
              maxValue={calculations.totalSavings}
            />
          </FormField>
        </SpaceBetween>

        {/* Calculator Inputs */}
        <SpaceBetween size="s">
          <Box variant="h4">Calculator</Box>
          <ColumnLayout columns={2}>
            <FormField label="Current Infrastructure Cost">
              <Input
                type="number"
                value={inputs.currentInfrastructureCost.toString()}
                onChange={({ detail }) => 
                  setInputs(prev => ({
                    ...prev,
                    currentInfrastructureCost: Number(detail.value)
                  }))
                }
              />
            </FormField>
            <FormField label="Expected Infrastructure Cost">
              <Input
                type="number"
                value={inputs.expectedInfrastructureCost.toString()}
                onChange={({ detail }) =>
                  setInputs(prev => ({
                    ...prev,
                    expectedInfrastructureCost: Number(detail.value)
                  }))
                }
              />
            </FormField>
          </ColumnLayout>
          <ColumnLayout columns={2}>
            <FormField label="Developer Hour Rate">
              <Input
                type="number"
                value={inputs.developmentHourRate.toString()}
                onChange={({ detail }) =>
                  setInputs(prev => ({
                    ...prev,
                    developmentHourRate: Number(detail.value)
                  }))
                }
              />
            </FormField>
            <FormField label="Annual Deployments">
              <Input
                type="number"
                value={inputs.deploymentFrequency.toString()}
                onChange={({ detail }) =>
                  setInputs(prev => ({
                    ...prev,
                    deploymentFrequency: Number(detail.value)
                  }))
                }
              />
            </FormField>
          </ColumnLayout>
        </SpaceBetween>

        {/* Savings Trend */}
        <Box>
          <LineChart
            series={[
              {
                title: 'Monthly Savings',
                type: 'line',
                data: trendData,
                valueFormatter: value => `$${value.toLocaleString()}`
              }
            ]}
            xDomain={trendData.map(item => item.x)}
            yDomain={[0, 40000]}
            hideFilter
            height={200}
          />
        </Box>
      </SpaceBetween>
    </Container>
  );
};

export default CostSavingsEstimator;
